'use client';

import React, { useCallback, useMemo } from 'react';
import { Typography } from '@workspace/ui/common';
import type { Link } from '@workspace/interfaces';
import type { IMegaMenuNavigationItemColumn } from '../interfaces';
import { getLinkProps, getActivePathFromLink } from '@/app/lib';
import { cn } from '@/app/lib/utils';
import NextLink from 'next/link';

interface INavigationItemSimpleProps {
  item: IMegaMenuNavigationItemColumn;
  index: number;
  isActive: boolean;
  onItemClick: (index: number, item: IMegaMenuNavigationItemColumn) => void;
}

const linkClasses = (active: boolean) =>
  cn(
    'h-21.25 relative block px-xs leading-5 text-base font-sora font-semibold no-underline cursor-pointer py-lg',
    'hover:text-primary-500',
    active ? 'text-primary font-semibold' : 'text-black',
    'transition-all duration-300 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-sm',
  );

export const NavigationItem = ({ item, index, isActive, onItemClick }: INavigationItemSimpleProps) => {
  const isHashOnly = useCallback((l?: Link) => {
    const href = getActivePathFromLink(l);
    return href === '' || href === '#';
  }, []);

  const hasChildren = useMemo(
    () => (item.childrenColumn?.length ?? 0) > 0 || (item.childrenGrid?.length ?? 0) > 0,
    [item.childrenColumn, item.childrenGrid],
  );

  const isLeafNavigable = useMemo(() => !hasChildren && !isHashOnly(item.link), [hasChildren, isHashOnly, item.link]);

  const isExternal = useMemo(
    () => Boolean(item.link && (item.link.isOpenInNewTab || item.link.type === 'external')),
    [item.link],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isLeafNavigable) {
        e.preventDefault();
        // Only call the callback for items with children or hash-only links (like original NavigationItem)
        onItemClick(index, item);
      }
      // For leaf navigable items, let the normal navigation happen
      onItemClick(index, item);
    },
    [isLeafNavigable, onItemClick, index, item],
  );

  const linkProps = useMemo(() => {
    const prop = getLinkProps(item.link);
    return {
      href: prop.href || '#',
      ...prop,
    };
  }, [item.link]);

  return (
    <li className="group" role="none" data-index={index} data-nav-item="true">
      {isExternal ? (
        <NextLink
          {...linkProps}
          className={linkClasses(isActive)}
          role="menuitem"
          aria-expanded={hasChildren ? isActive : undefined}
          data-active={isActive ? 'true' : undefined}
          onClick={handleClick}
        >
          {item.label}
        </NextLink>
      ) : (
        <Typography
          variant="link"
          {...getLinkProps(item.link)}
          className={linkClasses(isActive)}
          role="menuitem"
          aria-expanded={hasChildren ? isActive : undefined}
          data-active={isActive ? 'true' : undefined}
          onClick={handleClick}
        >
          {item.label}
        </Typography>
      )}
    </li>
  );
};
